import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { ApiConflictError, ApiError } from '../../../apiClients/BaseApiClient';
import i18n from '../../../i18n';
import {
  formatAmount,
  getTransactionTypeDisplayName,
} from '../../../lib/utils';
import { notificationStore } from '../../../stores/NotificationStore';
import { billingApi } from '../api/billingApi';
import {
  AccountType,
  CreateChargeRequest,
  CreateDepositRequest,
  GetPaymentsParams,
  Payment,
  PaymentsCursor,
  TransactionEventType,
  UserAccountData,
} from '../api/types';

export class BillingStore {
  @observable payments: Payment[] = [];
  @observable userAccounts: Map<string, UserAccountData> = new Map();
  @observable cursor: PaymentsCursor | null = null;
  @observable hasMore = true;
  @observable isLoading = false;
  @observable isLoadingAccounts = false;
  @observable isSubmitting = false;
  @observable error: Error | null = null;
  @observable accountsError: Error | null = null;

  // Filters
  @observable currentUserId: string | null = null;
  @observable currentTransactionType: TransactionEventType | null = null;

  // UI State
  @observable activeTab: 'transactions' | 'balances' = 'transactions';
  @observable balancesSortBy: 'name' | 'balance' = 'name';
  @observable balancesSortOrder: 'asc' | 'desc' = 'asc';
  @observable balancesSearchQuery: string = '';

  constructor() {
    makeObservable(this);
  }

  // ===============================
  // COMPUTED PROPERTIES
  // ===============================

  @computed get isError(): boolean {
    return this.error !== null;
  }

  @computed get hasData(): boolean {
    return this.payments.length > 0;
  }

  @computed get isAccountsError(): boolean {
    return this.accountsError !== null;
  }

  @computed get hasAccountsData(): boolean {
    return this.userAccounts.size > 0;
  }

  @computed get currentUserBalance(): string {
    if (!this.currentUserId) return '0';

    const accountData = this.userAccounts.get(this.currentUserId);
    if (!accountData || !accountData.accounts.length) return '0';

    // Find the main account (AccountType.Main = 1)
    const mainAccount = accountData.accounts.find(
      (account) => account.typeId === AccountType.Main
    );
    // If not found, use the first account as fallback
    const account = mainAccount || accountData.accounts[0];

    return account ? formatAmount(account.balance) : '0';
  }

  @computed get currentUserPendingBalance(): string {
    if (!this.currentUserId) return '0';

    const accountData = this.userAccounts.get(this.currentUserId);
    return accountData ? formatAmount(accountData.pendingBalance) : '0';
  }

  @computed get totalPendingBalance(): string {
    let total = 0;

    this.userAccounts.forEach((accountData) => {
      const pendingAmount = Number(accountData.pendingBalance || '0');
      if (!Number.isNaN(pendingAmount)) {
        total += pendingAmount;
      }
    });

    return formatAmount(String(total));
  }

  @computed get filteredPayments(): Payment[] {
    // Since we now filter on the server side when a filter is selected,
    // we can return payments as-is. The server already filtered them.
    return this.payments;
  }

  // ===============================
  // ACTIONS
  // ===============================

  @action setUserId(userId: string | null): void {
    this.currentUserId = userId;
  }

  @action setTransactionType(type: TransactionEventType | null): void {
    this.currentTransactionType = type;
  }

  @action setActiveTab(tab: 'transactions' | 'balances'): void {
    this.activeTab = tab;
  }

  @action setBalancesSort(
    sortBy: 'name' | 'balance',
    sortOrder: 'asc' | 'desc'
  ): void {
    this.balancesSortBy = sortBy;
    this.balancesSortOrder = sortOrder;
  }

  @action setBalancesSearchQuery(query: string): void {
    this.balancesSearchQuery = query;
  }

  @action clearFilters(): void {
    this.currentUserId = null;
    this.currentTransactionType = null;
  }

  async loadPayments(reset = false): Promise<void> {
    if (this.isLoading) return;

    if (reset) {
      runInAction(() => {
        this.payments = [];
        this.cursor = null;
        this.hasMore = true;
      });
    }

    if (!this.hasMore) return;

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const params: GetPaymentsParams = {
        userId: this.currentUserId || undefined,
        cursor: this.cursor || undefined,
        limit: 25,
      };

      // Add typeId filter only if specific type is selected
      if (this.currentTransactionType !== null) {
        params.typeId = this.currentTransactionType;
      }

      const response = await billingApi.getPayments(params);

      runInAction(() => {
        if (reset) {
          this.payments = response.payments;
        } else {
          // Only add payments if there are any
          if (response.payments.length > 0) {
            this.payments.push(...response.payments);
          }
        }
        this.cursor = response.cursor;
        this.hasMore = response.cursor !== null && response.payments.length > 0;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
      notificationStore.showError(i18n.t('errors.failedToLoadBillingData'));
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async loadUserAccounts(userId?: string): Promise<void> {
    if (this.isLoadingAccounts) return;

    runInAction(() => {
      this.isLoadingAccounts = true;
      this.accountsError = null;
    });

    try {
      const response = await billingApi.getUserAccounts(
        userId ? { userId } : undefined
      );

      runInAction(() => {
        this.userAccounts.clear();
        response.data.forEach((accountData) => {
          this.userAccounts.set(accountData.userId, accountData);
        });
      });
    } catch (error) {
      runInAction(() => {
        this.accountsError = error as Error;
      });
      notificationStore.showError(i18n.t('errors.failedToLoadAccountData'));
    } finally {
      runInAction(() => {
        this.isLoadingAccounts = false;
      });
    }
  }

  async createDeposit(request: CreateDepositRequest): Promise<boolean> {
    if (this.isSubmitting) return false;

    runInAction(() => {
      this.isSubmitting = true;
      this.error = null;
    });

    try {
      await billingApi.createDeposit(request);

      notificationStore.showSuccess(i18n.t('billing.depositCreatedSuccess'));

      // Reload data
      await this.loadPayments(true);
      await this.loadUserAccounts(request.userId);

      return true;
    } catch (error) {
      this.error = error as Error;
      const errorMessage =
        error instanceof ApiError
          ? error.errorMessage
          : i18n.t('errors.failedToCreateDeposit');
      notificationStore.showError(errorMessage);
      return false;
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  }

  async createCharge(request: CreateChargeRequest): Promise<boolean> {
    if (this.isSubmitting) return false;

    runInAction(() => {
      this.isSubmitting = true;
      this.error = null;
    });

    try {
      await billingApi.createCharge(request);

      notificationStore.showSuccess(i18n.t('billing.chargeCreatedSuccess'));

      // Reload data
      await this.loadPayments(true);
      await this.loadUserAccounts(request.userId);

      return true;
    } catch (error) {
      this.error = error as Error;
      const errorMessage =
        error instanceof ApiError
          ? error.errorMessage
          : i18n.t('errors.failedToCreateCharge');
      notificationStore.showError(errorMessage);
      return false;
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  }

  async approveDeposit(eventId: string): Promise<boolean> {
    if (this.isSubmitting) return false;

    runInAction(() => {
      this.isSubmitting = true;
      this.error = null;
    });

    try {
      await billingApi.approveDeposit({ eventId });

      notificationStore.showSuccess(i18n.t('billing.depositApprovedSuccess'));

      // Reload data silently without showing loading spinner
      await this.reloadDataSilently();

      return true;
    } catch (error) {
      this.error = error as Error;

      // Handle 409 Conflict - deposit was already updated by someone else
      if (error instanceof ApiConflictError) {
        notificationStore.showInfo(i18n.t('billing.depositAlreadyUpdated'));
        // Reload data to show current status
        await this.reloadDataSilently();
        return true;
      }

      const errorMessage =
        error instanceof ApiError
          ? error.errorMessage
          : i18n.t('errors.failedToApproveDeposit');
      notificationStore.showError(errorMessage);
      return false;
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  }

  async cancelDeposit(eventId: string): Promise<boolean> {
    if (this.isSubmitting) return false;

    runInAction(() => {
      this.isSubmitting = true;
      this.error = null;
    });

    try {
      await billingApi.cancelDeposit({ eventId });

      notificationStore.showSuccess(i18n.t('billing.depositCancelledSuccess'));

      // Reload data silently without showing loading spinner
      await this.reloadDataSilently();

      return true;
    } catch (error) {
      this.error = error as Error;

      // Handle 409 Conflict - deposit was already updated by someone else
      if (error instanceof ApiConflictError) {
        notificationStore.showInfo(i18n.t('billing.depositAlreadyUpdated'));
        // Reload data to show current status
        await this.reloadDataSilently();
        return true;
      }

      const errorMessage =
        error instanceof ApiError
          ? error.errorMessage
          : i18n.t('errors.failedToCancelDeposit');
      notificationStore.showError(errorMessage);
      return false;
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  }

  /**
   * Reload data silently without showing loading spinner to avoid page flickering
   */
  @action async reloadDataSilently(): Promise<void> {
    try {
      // Load fresh data without clearing current data first
      const params: GetPaymentsParams = {
        userId: this.currentUserId || undefined,
        limit: 25,
      };

      if (this.currentTransactionType !== null) {
        params.typeId = this.currentTransactionType;
      }

      const [response, accountsResponse] = await Promise.all([
        billingApi.getPayments(params),
        billingApi.getUserAccounts(),
      ]);

      runInAction(() => {
        // Only update after we have fresh data
        this.payments = response.payments;
        this.cursor = response.cursor;
        this.hasMore = response.cursor !== null && response.payments.length > 0;
        this.error = null;

        // Update user accounts
        this.userAccounts.clear();
        accountsResponse.data.forEach((accountData) => {
          this.userAccounts.set(accountData.userId, accountData);
        });
        this.accountsError = null;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
    }
  }

  /**
   * Reload only payments silently (used on filter change) to avoid flicker
   */
  @action async reloadPaymentsSilently(): Promise<void> {
    try {
      const params: GetPaymentsParams = {
        userId: this.currentUserId || undefined,
        limit: 25,
      };
      if (this.currentTransactionType !== null) {
        params.typeId = this.currentTransactionType;
      }

      const response = await billingApi.getPayments(params);

      runInAction(() => {
        this.payments = response.payments;
        this.cursor = response.cursor;
        this.hasMore = response.cursor !== null && response.payments.length > 0;
        this.error = null;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
    }
  }

  @action clear(): void {
    this.payments = [];
    this.userAccounts.clear();
    this.cursor = null;
    this.hasMore = true;
    this.isLoading = false;
    this.isLoadingAccounts = false;
    this.isSubmitting = false;
    this.error = null;
    this.accountsError = null;
    this.currentUserId = null;
    this.currentTransactionType = null;
  }

  // ===============================
  // UTILITY METHODS
  // ===============================

  isDepositPending(payment: Payment): boolean {
    return (
      payment.typeId === TransactionEventType.Deposit &&
      this.currentUserPendingBalance !== '0.00'
    );
  }

  getTransactionTypeDisplayName(type: TransactionEventType): string {
    return getTransactionTypeDisplayName(type);
  }

  // Get user ID from payment transactions
  getUserIdFromPayment(payment: Payment): string | null {
    // For EventCharge, parse from externalId
    if (
      payment.typeId === TransactionEventType.EventCharge &&
      payment.externalId
    ) {
      const parsed = billingApi.parseExternalId(payment.externalId);
      return parsed?.userId || null;
    }

    // For other types, we might need to infer from account info
    // This is a simplified approach - in real app you might store userId in payment
    return null;
  }

  // ===============================
  // LIFECYCLE METHODS
  // ===============================

  async initialize(userId?: string): Promise<void> {
    this.setUserId(userId || null);
    await Promise.all([this.loadPayments(true), this.loadUserAccounts(userId)]);
  }

  destroy(): void {
    this.clear();
  }
}

// Export singleton instance
export const billingStore = new BillingStore();
